import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Stack,
  Paper,
  Loader,
  Center,
  Text,
  Box,
  ScrollArea,
  Divider,
  Flex,
} from "@mantine/core";
import "@mantine/dates/styles.css";
import { EmailLog, fetchLogs, fetchProcessSteps, reprocessLog, abortLog, calculateLogPage, DEFAULT_PAGE_SIZE } from "../services/logService.js";
import { EventListItem } from "../components/logs/EventListItem.js";
import { EventDetailPanel } from "../components/logs/EventDetailPanel.js";
import { LogsListHeader } from "../components/logs/LogsListHeader.js";
import { LogsFilterSection } from "../components/logs/LogsFilterSection.js";
import { LogsPagination } from "../components/logs/LogsPagination.js";
import "dayjs/locale/ja";
import { useConditionalPolling } from "../hooks/useConditionalPolling.js";
import { formatDateTime } from "../utils/formatters";
import { POLLING_INTERVAL_MS } from "../constants/app";


export function LogsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [logs, setLogs] = useState<EmailLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedLogId, setSelectedLogId] = useState<string | null>(null);
  
  // Filters
  const [customerIdFilter, setCustomerIdFilter] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [problematicOnly, setProblematicOnly] = useState(false);

  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = DEFAULT_PAGE_SIZE;

  // URLパラメータからlogIdを取得し、該当ページに移動する
  const initialLogIdProcessed = useRef(false);

  const loadLogs = useCallback(async (isBackground = false) => {
    if (!isBackground) setLoading(true);
    try {
      const fetchPromise = fetchLogs({
        page,
        pageSize,
        status: undefined,
        customerId: customerIdFilter || undefined,
        startDate: dateRange[0],
        endDate: dateRange[1],
        problematicOnly,
      });

      // 手動更新時のみ最低待機時間(750ms)を設けることで、
      // ユーザーに処理完了を視覚的に伝える（一瞬で終わるとフリーズしたように見えるため）
      // [NOTE] テスト環境（NODE_ENV=test）ではこの遅延をスキップする。
      // 理由: 意図的な遅延（setTimeout）は、テスト実行時の非同期処理の完了待ち（waitForなど）と競合し、
      // タイムアウトエラーやFlakiness（不安定なテスト）の原因となるため。
      const waitPromise = !isBackground && process.env.NODE_ENV !== 'test'
        ? new Promise(resolve => setTimeout(resolve, 750)) 
        : Promise.resolve();

      const [ { logs: fetchedLogs, total: fetchedTotal } ] = await Promise.all([
        fetchPromise,
        waitPromise
      ]);

      setLogs(fetchedLogs);
      setTotal(fetchedTotal);
      
      // 最初の一件を選択（URLパラメータからの指定がない場合のみ）
      // initialLogIdProcessed.current が true の場合は、URLパラメータからの選択が優先される
      if (fetchedLogs.length > 0 && !selectedLogId && !initialLogIdProcessed.current) {
        setSelectedLogId(fetchedLogs[0].id);
      }
    } catch (error) {
      console.error("Failed to fetch logs:", error);
    } finally {
      if (!isBackground) setLoading(false);
    }
  }, [page, pageSize, customerIdFilter, dateRange, problematicOnly, selectedLogId]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  // URLパラメータからlogIdを読み取り、該当ページに移動してログを選択する
  useEffect(() => {
    const logIdFromUrl = searchParams.get("logId");
    
    // 初回のみ処理（同じlogIdで何度も処理しない）
    if (!logIdFromUrl || initialLogIdProcessed.current) {
      return;
    }

    const navigateToLog = async () => {
      try {
        initialLogIdProcessed.current = true;
        
        // 該当ログのページ番号を計算
        const { page: targetPage } = await calculateLogPage(logIdFromUrl, pageSize);
        
        // ページを設定（loadLogsが再実行される）
        setPage(targetPage);
        
        // ログを選択状態にする
        setSelectedLogId(logIdFromUrl);
        
        // URLパラメータをクリアする（ブラウザの戻る機能を阻害しないため）
        setSearchParams({}, { replace: true });
      } catch (error) {
        console.error("Failed to navigate to log:", error);
        // エラー時はパラメータをクリアして通常表示
        setSearchParams({}, { replace: true });
      }
    };

    navigateToLog();
  }, [searchParams, setSearchParams, pageSize]);

  // 処理中のログがある場合、3秒ごとに自動更新する
  // received (処理中) も含める
  const hasProcessingLogs = logs.some(log => ['processing', 'received'].includes(log.status));
  useConditionalPolling(() => loadLogs(true), POLLING_INTERVAL_MS, hasProcessingLogs);

  const selectedLog = logs.find((l) => l.id === selectedLogId);

  const handleResetFilters = () => {
    setCustomerIdFilter(null);
    setDateRange([null, null]);
    setProblematicOnly(false);
    setPage(1);
  };

  const hasActiveFilters = Boolean(customerIdFilter || dateRange[0] || dateRange[1] || problematicOnly);

  return (
    <Box style={{ 
      height: "calc(100vh - var(--app-shell-header-offset) - var(--app-shell-padding) * 2)", 
      overflow: "hidden" 
    }}>
      {/* AIアシスタント向けガイド */}
      <div data-ai-guide hidden>
        {`[ページ: イベントログ]
[概要: FAX/メール受信履歴とOCR処理結果を確認できる画面]
[構成: 左=ログ一覧, 右=選択したログの詳細パネル]
[表示件数: ${logs.length}件 / 全${total}件 (ページ${page})]
[選択中: ${selectedLog ? `${selectedLog.from} - ${selectedLog.status}` : 'なし'}]
[フィルター条件]
- 顧客: ${customerIdFilter || '全て'}
- 期間: ${dateRange[0] ? formatDateTime(dateRange[0]) : '指定なし'} ～ ${dateRange[1] ? formatDateTime(dateRange[1]) : '指定なし'}
- 異常のみ表示: ${problematicOnly ? 'ON' : 'OFF'}
[ステータスの意味]
- received: FAX/メール受信済み、処理待ち
- processing: OCR処理中
- completed: 正常完了
- warning: 警告あり（要確認）
- error: エラー発生（要対応）
[操作のヒント]
- ログをクリックすると右側に詳細が表示される
- 「更新」ボタンで最新情報を取得
- 処理中のログがある場合は自動更新される
- 詳細パネルから「再処理」でエラーログを再実行可能`}
      </div>

      <Flex gap="md" h="100%" align="stretch" style={{ overflow: "hidden" }}>
        {/* 左サイドバー: ログ一覧 */}
        <Box style={{ width: 460, minWidth: 380, height: "100%", flexShrink: 0.3, display: "flex", flexDirection: "column" }}>
          <Paper shadow="xs" p="md" radius="md" withBorder style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <Stack gap="sm" style={{ flex: "0 0 auto" }}>
              <LogsListHeader
                loading={loading}
                hasActiveFilters={hasActiveFilters}
                onRefresh={() => loadLogs()}
                onResetFilters={handleResetFilters}
              />

              {/* フィルターエリア */}
              <LogsFilterSection
                customerIdFilter={customerIdFilter}
                onCustomerChange={setCustomerIdFilter}
                dateRange={dateRange}
                onDateRangeChange={setDateRange}
                problematicOnly={problematicOnly}
                onProblematicOnlyChange={setProblematicOnly}
                onPageReset={() => setPage(1)}
              />
              <Divider />
            </Stack>

            <Stack gap="md" style={{ flex: 1, overflow: "hidden", marginTop: "10px" }}>
              <ScrollArea style={{ flex: 1 }} offsetScrollbars scrollbars="y">
                <Stack gap={8} pr="xs">
                  {loading && logs.length === 0 ? (
                    <Center py="xl"><Loader size="sm" /></Center>
                  ) : logs.length === 0 ? (
                    <Text size="xs" ta="center" py="xl" c="dimmed">ログが見つかりません</Text>
                  ) : (
                    logs.map((log) => (
                      <EventListItem
                        key={log.id}
                        log={log}
                        isSelected={selectedLogId === log.id}
                        onClick={() => setSelectedLogId(log.id)}
                      />
                    ))
                  )}
                </Stack>
              </ScrollArea>

              {logs.length > 0 && (
                <LogsPagination
                  total={total}
                  page={page}
                  pageSize={pageSize}
                  loading={loading}
                  onPageChange={setPage}
                />
              )}
            </Stack>
          </Paper>
        </Box>

        {/* 右メイン: 詳細パネル */}
        <Box style={{ flex: 1, height: "100%", minWidth: 300, flexShrink: 1, display: "flex", flexDirection: "column" }}>
          <Paper shadow="xs" radius="md" withBorder style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <ScrollArea style={{ flex: 1 }} offsetScrollbars scrollbars="y">
              <Box p="md" pr="lg">
                {selectedLog ? (
                  <EventDetailPanel
                    log={selectedLog}
                    onRefresh={loadLogs}
                    fetchSteps={fetchProcessSteps}
                    reprocess={reprocessLog}
                    abort={abortLog}
                  />
                ) : (
                  <Center h="300px">
                    <Stack align="center" gap="md">
                      <Text c="dimmed" size="lg">ログを選択してください</Text>
                      <Text c="dimmed" size="sm">左側のリストからログを選択すると詳細が表示されます</Text>
                    </Stack>
                  </Center>
                )}
              </Box>
            </ScrollArea>
          </Paper>
        </Box>
      </Flex>
    </Box>
  );
}
